import React, { useEffect, useState } from 'react';
import { Trash2, Plus, X, MessageSquare, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { tasksAPI } from '@/lib/api';
import { parseDue, ymd, dayToISO } from './taskUtils';

const NONE = '__none__';
const STATUSES = [['backlog', 'To do'], ['in_progress', 'In progress'], ['review', 'Review'], ['done', 'Done']];
const PRIORITIES = [['low', 'Low'], ['medium', 'Normal'], ['high', 'High'], ['urgent', 'Urgent']];
const label = 'text-[11px] uppercase tracking-wider font-semibold text-[#5C6670]';

// Everything about one task on a single scrolling panel. Edits save as you go (text fields when you leave them).
export const Checklist = ({ task, onCount, compact = false }) => {
  const [items, setItems] = useState(null);
  const [text, setText] = useState('');
  useEffect(() => {
    tasksAPI.getSubtasks(task._id).then((r) => setItems(r.data || [])).catch(() => setItems([]));
  }, [task._id]);
  useEffect(() => { if (items && onCount) onCount(items.length, items.filter((i) => i.status === 'done').length); }, [items]); // eslint-disable-line react-hooks/exhaustive-deps
  const add = async () => {
    const title = text.trim();
    if (!title) return;
    setText('');
    try { const r = await tasksAPI.createSubtask(task._id, { title }); setItems((x) => [...(x || []), r.data]); } catch { toast.error('Could not add that step'); }
  };
  const toggle = async (it) => {
    const status = it.status === 'done' ? 'backlog' : 'done';
    setItems((x) => x.map((i) => (i._id === it._id ? { ...i, status } : i)));
    try { await tasksAPI.updateSubtask(task._id, it._id, { status }); } catch { toast.error('Could not update that step'); }
  };
  const remove = async (it) => {
    setItems((x) => x.filter((i) => i._id !== it._id));
    try { await tasksAPI.deleteSubtask(task._id, it._id); } catch { toast.error('Could not remove that step'); }
  };
  if (!items) return <div className="text-xs text-[#94A3B8] py-1">Loading…</div>;
  return (
    <div className="space-y-0.5">
      {items.map((it) => (
        <div key={it._id} className="group flex items-center gap-2 py-0.5">
          <input type="checkbox" className="h-3.5 w-3.5 accent-[#A62B1F]" checked={it.status === 'done'} onChange={() => toggle(it)} />
          <span className={`flex-1 text-sm ${it.status === 'done' ? 'line-through text-[#94A3B8]' : 'text-[#1E2A33]'}`}>{it.title}</span>
          <button type="button" className="opacity-0 group-hover:opacity-100 text-[#94A3B8] hover:text-[#A62B1F]" onClick={() => remove(it)} aria-label="Remove step"><X className="w-3.5 h-3.5" /></button>
        </div>
      ))}
      <div className="flex items-center gap-2 pt-0.5">
        <Plus className="w-3.5 h-3.5 text-[#94A3B8]" />
        <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder={items.length ? 'Add a step' : 'Add a checklist step, press Enter'} className={`flex-1 bg-transparent outline-none ${compact ? 'text-xs' : 'text-sm'} placeholder:text-[#B5AC9C]`} />
      </div>
    </div>
  );
};

export default function TaskPanel({ task, open, onOpenChange, onChanged, onDeleted, projects = [], lists = [], roster = [] }) {
  const [f, setF] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [update, setUpdate] = useState('');
  useEffect(() => {
    if (!task) return;
    setF({ title: task.title || '', description: task.description || '' });
    setUpdates([]);
    if (open) tasksAPI.getProgress(task._id).then((r) => setUpdates(r.data || [])).catch(() => {});
  }, [task?._id, open]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!task || !f) return null;

  const save = async (changes) => {
    onChanged({ ...task, ...changes });
    try { const r = await tasksAPI.update(task._id, changes); onChanged({ ...task, ...changes, ...(r.data || {}) }); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not save'); onChanged(task); }
  };
  const saveText = (k) => { const v = k === 'title' ? f.title.trim() : f[k]; if (k === 'title' && !v) { setF({ ...f, title: task.title }); return; } if (v !== (task[k] || '')) save({ [k]: v || (k === 'description' ? null : v) }); };
  const addUpdate = async () => {
    const note = update.trim();
    if (!note) return;
    try { const r = await tasksAPI.addProgress(task._id, { note }); setUpdates((u) => [r.data, ...u]); setUpdate(''); } catch { toast.error('Could not add the update'); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete "${task.title}"?`)) return;
    try { await tasksAPI.delete(task._id); onDeleted(task._id); onOpenChange(false); toast.success('Task deleted'); } catch { toast.error('Could not delete it'); }
  };
  const due = parseDue(task.due_date);
  const assigneeKnown = !task.assigned_to || roster.some((m) => m.email === task.assigned_to);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader><SheetTitle className="sr-only">Task</SheetTitle></SheetHeader>
        <div className="flex items-start gap-3 mt-2">
          <input type="checkbox" className="mt-2 h-5 w-5 accent-[#A62B1F]" checked={task.status === 'done'} onChange={() => save({ status: task.status === 'done' ? 'backlog' : 'done' })} aria-label="Done" />
          <Textarea value={f.title} rows={1} onChange={(e) => setF({ ...f, title: e.target.value })} onBlur={() => saveText('title')}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }}
            className={`text-lg font-semibold border-0 shadow-none px-1 resize-none focus-visible:ring-1 min-h-0 ${task.status === 'done' ? 'line-through text-[#94A3B8]' : 'text-[#1E2A33]'}`} />
        </div>

        <div className="grid grid-cols-2 gap-3 mt-4">
          <div><div className={label}>Due</div>
            <div className="flex items-center gap-1 mt-1">
              <Input type="date" value={ymd(due)} onChange={(e) => save({ due_date: dayToISO(e.target.value) })} className="h-9" />
              {due && <button type="button" className="text-[#94A3B8] hover:text-[#A62B1F]" onClick={() => save({ due_date: null })} aria-label="Clear date"><X className="w-4 h-4" /></button>}
            </div></div>
          <div><div className={label}>Who</div>
            <Select value={task.assigned_to || NONE} onValueChange={(v) => save({ assigned_to: v === NONE ? null : v })}>
              <SelectTrigger className="h-9 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>Me / nobody yet</SelectItem>
                {roster.map((m) => <SelectItem key={m.email} value={m.email}>{m.full_name || m.email}</SelectItem>)}
                {!assigneeKnown && <SelectItem value={task.assigned_to}>{task.assigned_to}</SelectItem>}
              </SelectContent>
            </Select></div>
          <div><div className={label}>Project</div>
            <Select value={task.project_id || NONE} onValueChange={(v) => save({ project_id: v === NONE ? null : v })}>
              <SelectTrigger className="h-9 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>No project</SelectItem>
                {projects.map((p) => <SelectItem key={p._id} value={p._id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select></div>
          <div><div className={label}>List</div>
            <Select value={task.list_id || NONE} onValueChange={(v) => save({ list_id: v === NONE ? null : v })}>
              <SelectTrigger className="h-9 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>Not in a list</SelectItem>
                {lists.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select></div>
          <div><div className={label}>Status</div>
            <Select value={task.status || 'backlog'} onValueChange={(v) => save({ status: v })}>
              <SelectTrigger className="h-9 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><div className={label}>Priority</div>
            <Select value={task.priority || 'medium'} onValueChange={(v) => save({ priority: v })}>
              <SelectTrigger className="h-9 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{PRIORITIES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select></div>
        </div>

        <div className="mt-5"><div className={`${label} mb-1`}>Checklist</div><Checklist task={task} onCount={(n, d) => (n !== task.subtask_count || d !== task.completed_subtasks) && onChanged({ ...task, subtask_count: n, completed_subtasks: d })} /></div>

        <div className="mt-5"><div className={`${label} mb-1 flex items-center gap-1`}><FileText className="w-3 h-3" />Notes</div>
          <Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} onBlur={() => saveText('description')} rows={4} placeholder="Details, links, anything useful" /></div>

        <div className="mt-5"><div className={`${label} mb-1 flex items-center gap-1`}><MessageSquare className="w-3 h-3" />Updates</div>
          <div className="flex gap-2">
            <Input value={update} onChange={(e) => setUpdate(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addUpdate()} placeholder="Where it's at (press Enter)" />
          </div>
          {updates.map((u) => (
            <div key={u._id} className="text-sm border-l-2 border-[#E3DDD1] pl-3 mt-2">
              <div className="text-[#1E2A33] whitespace-pre-line">{u.note}</div>
              <div className="text-[11px] text-[#94A3B8]">{u.created_by_name || u.user_name || ''} {u.created_at ? parseDue(u.created_at).toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}</div>
            </div>
          ))}
        </div>

        <div className="flex justify-between items-center mt-6 pt-4 border-t border-[#E3DDD1]">
          <span className="text-[11px] text-[#94A3B8]">{task.created_at ? `Added ${parseDue(task.created_at).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}` : ''}</span>
          <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={remove}><Trash2 className="w-4 h-4 mr-1" />Delete</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
